// In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.
// NexoraX NexoraX Voice Interface - "Out of Galaxy" Aesthetics
// Feature: Live Neural Waveform + Engagement Social Engine + Live Voice Recorder
// Intellectual Property Status: Patent-Readiness Protected Assets, © NexoraX Digital Ltd (SC887095)

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Mic, Activity, Radio, Volume2, ShieldAlert, Sparkles, Heart, 
  Sun, MapPin, Building, CreditCard, ChevronRight, Play, Square, 
  Trash2, RefreshCw, AudioLines, CheckCircle2, AlertTriangle, FileText,
  Server, Globe, Terminal, Shield, Cpu, Check, Settings
} from 'lucide-react';
import { AudioVisualizerCanvas } from './AudioVisualizerCanvas';
import { pcmBase64ToWavDataUrl } from '../lib/audioWav';

interface WeatherOption {
  city: string;
  weather: string;
}

// ========================================================
// NEXORAX VOICE AUTOTUNE SYSTEM CONFIG (NODE-09.2)
// Array of musical frequencies mapped to unique robotic scales.
// ========================================================
const SCALE_FREQUENCIES: Record<string, { name: string; hz: number }[]> = {
  cyberpunk_minor: [
    { name: "A3", hz: 220.00 },
    { name: "C4", hz: 261.63 },
    { name: "D4", hz: 293.66 },
    { name: "E4", hz: 329.63 },
    { name: "G4", hz: 392.00 },
    { name: "A4", hz: 440.00 },
    { name: "C5", hz: 523.25 },
    { name: "D5", hz: 587.33 }
  ],
  NexoraX_major: [
    { name: "C3", hz: 130.81 },
    { name: "E3", hz: 164.81 },
    { name: "G3", hz: 196.00 },
    { name: "C4", hz: 261.63 },
    { name: "E4", hz: 329.63 },
    { name: "G4", hz: 392.00 },
    { name: "C5", hz: 523.25 }
  ],
  pentatonic_classic: [
    { name: "D3", hz: 146.83 },
    { name: "F3", hz: 174.61 },
    { name: "G3", hz: 196.00 },
    { name: "A3", hz: 220.00 },
    { name: "C4", hz: 261.63 },
    { name: "D4", hz: 293.66 },
    { name: "F4", hz: 349.23 }
  ],
  matrix_space: [
    { name: "E3", hz: 164.81 },
    { name: "A3", hz: 220.00 },
    { name: "B3", hz: 246.94 },
    { name: "C4", hz: 261.63 },
    { name: "E4", hz: 329.63 },
    { name: "F4", hz: 349.23 },
    { name: "A4", hz: 440.00 }
  ]
};

export const NexoraXVoiceAssistant: React.FC = () => {
  // Modes: 'synthesis' (TTS) or 'mic_capture' (Real Recording)
  const [activeMode, setActiveMode] = useState<'synthesis' | 'mic_capture'>('mic_capture');
  
  const [isActive, setIsActive] = useState(true);
  const [bars, setBars] = useState<number[]>(Array(36).fill(15));
  const [voiceLog, setVoiceLog] = useState<string>("SYSTEM STANDBY // IDLE TRANSMISSION CONDUIT");
  const [speechText, setSpeechText] = useState<string>("SYSTEM STANDBY");
  const [selectedCityIdx, setSelectedCityIdx] = useState<number>(0);
  const [customBusiness, setCustomBusiness] = useState<string>("Pinnacle Digital");
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false);
  const [isTtsMuted, setIsTtsMuted] = useState<boolean>(false);
  
  // Real-Time Microphone capture & recording state
  const [voiceState, setVoiceState] = useState<'idle' | 'recording' | 'processing'>('idle');
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState<string | null>(null);
  const [isPlaybackPlaying, setIsPlaybackPlaying] = useState<boolean>(false);
  const [micStateMsg, setMicStateMsg] = useState<string>("No active input stream connected.");
  const [hasMicPermissionError, setHasMicPermissionError] = useState<boolean>(false);
  const [transcriptionResult, setTranscriptionResult] = useState<string>("");
  const [isTranscribing, setIsTranscribing] = useState<boolean>(false);

  // ========================================================
  // NEXORAX INTENT PARSING ENGINE (NODE-09.2 ANTIGRAVITY)
  // Identifies keywords like 'deploy', 'audit', or 'status'
  // and triggers physical component operations.
  // ========================================================
  const [parsedIntent, setParsedIntent] = useState<'idle' | 'deploying' | 'auditing' | 'status_checking'>('idle');
  const [typedCommand, setTypedCommand] = useState<string>("");
  const [deployStep, setDeployStep] = useState<number>(0);
  const [deployLogs, setDeployLogs] = useState<string[]>([]);
  const [customAuditGross, setCustomAuditGross] = useState<string>("2820000");
  const [auditBreakdown, setAuditBreakdown] = useState<{
    gross: number;
    levy: number;
    charity1: number;
    charity2: number;
    charity3: number;
    taxBase: number;
    liability20: number;
    netToSecurePayment: number;
  } | null>(null);

  const parseTextForIntents = (rawText: string) => {
    const text = rawText.toLowerCase();

    // Dedicated Concierge Node command patterns and sequence triggers
    if (text.includes("concierge node") || text.includes("concierge mode") || text.includes("activate concierge") || text.includes("NexoraX concierge")) {
      setVocalPersona('concierge');
      setVoiceLog("VOCAL PERSONA: CONCIERGE-CLASS [ACTIVE]");
      const welcomeText = "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds. Welcome to the NexoraX Sanctum. I'm your NexoraX Concierge Node. Ready to guide your elite corporate routing, subsidies, and global settlements.";
      setSpeechText(welcomeText);
      setIsActive(true);
      executeTTS(welcomeText, "CONCIERGE_GREETING");
      return;
    }

    // Concierge-specific task routing
    if (vocalPersona === 'concierge') {
      if (text.includes("settlement") || text.includes("bank") || text.includes("SecurePayment") || text.includes("gavel")) {
        const textToSpeak = "Protocol acknowledged. Accessing Chronos Ledger to review Secure Payment Provider settlements and the active tiered corporate tax extraction ledger.";
        setSpeechText(textToSpeak);
        setIsActive(true);
        setVoiceLog("ROUTING: REALITY AUDIT LEDGER");
        executeTTS(textToSpeak, "CONCIERGE_ROUTING");
        window.dispatchEvent(new CustomEvent("nexorax-voice-navigate", {
          detail: { mainTab: "REALITY AUDIT LEDGER", landingTab: null }
        }));
        return;
      }
      if (text.includes("placement") || text.includes("Secure Invoice Register") || text.includes("candidate") || text.includes("recruit")) {
        const textToSpeak = "Initiating elite talent acquisition routing. Direct-billing to NexoraX billing records under NI reference NH 44 87 96 B has been initiated.";
        setSpeechText(textToSpeak);
        setIsActive(true);
        setVoiceLog("ROUTING: NEXORAX TALENT HUB");
        executeTTS(textToSpeak, "CONCIERGE_ROUTING");
        window.dispatchEvent(new CustomEvent("nexorax-voice-navigate", {
          detail: { mainTab: "NODE 0: ARCHITECT TERMINAL", landingTab: "NexoraX Talent Hub" }
        }));
        return;
      }
      if (text.includes("migration") || text.includes("compliance") || text.includes("Staff Coordinator")) {
        const textToSpeak = "Opening Chief of Staff secretary console to audit pending enterprise standards, compliance, and Pakistan Node Sync status lists.";
        setSpeechText(textToSpeak);
        setIsActive(true);
        setVoiceLog("ROUTING: SECRETARY NODE");
        executeTTS(textToSpeak, "CONCIERGE_ROUTING");
        window.dispatchEvent(new CustomEvent("nexorax-voice-navigate", {
          detail: { mainTab: "SECRETARY NODE", landingTab: null }
        }));
        return;
      }
    }
    
    if (text.includes("deploy") || text.includes("provision") || text.includes("launch")) {
      triggerNexoraXDeploy(rawText);
    } else if (text.includes("vault") || text.includes("open vault") || text.includes("access vault")) {
      const textToSpeak = "NexoraX Resource Library level cleared. Mirroring current background source synchronizations.";
      setSpeechText(textToSpeak);
      setIsActive(true);
      setVoiceLog("ROUTING: REALITY AUDIT LEDGER - NexoraX Resource Library");
      executeTTS(textToSpeak, "ROUTING_VAULT");
      window.dispatchEvent(new CustomEvent("nexorax-voice-navigate", {
        detail: { mainTab: "REALITY AUDIT LEDGER", landingTab: null }
      }));
    } else if (text.includes("scan infrastructure") || (text.includes("scan") && text.includes("infra"))) {
      const textToSpeak = "Scanning active sector grids. Node 01, 06, 16, and 17 return healthy telemetry indices.";
      setSpeechText(textToSpeak);
      setIsActive(true);
      setVoiceLog("ACTION: INFRASTRUCTURE SCAN");
      executeTTS(textToSpeak, "ROUTING_SCAN");
      window.dispatchEvent(new CustomEvent("nexorax-voice-navigate", {
        detail: { mainTab: "NODE 0: ARCHITECT TERMINAL", landingTab: "Home" }
      }));
    } else if (text.includes("report") || text.includes("generate report")) {
      const textToSpeak = "Financial extraction report compiled. Net settlements routed to trust ledger sequence.";
      setSpeechText(textToSpeak);
      setIsActive(true);
      setVoiceLog("ACTION: COMPILE REPORT");
      executeTTS(textToSpeak, "ROUTING_REPORT");
      window.dispatchEvent(new CustomEvent("nexorax-voice-navigate", {
        detail: { mainTab: "REALITY AUDIT LEDGER", landingTab: null }
      }));
    } else if (text.includes("knowledge base") || text.includes("knowledge") || text.includes("document")) {
      const textToSpeak = "Deploying NexoraX Knowledge Base panel for technical systems architectures.";
      setSpeechText(textToSpeak);
      setIsActive(true);
      setVoiceLog("ACTION: KNOWLEDGE BASE DEPLOY");
      executeTTS(textToSpeak, "ROUTING_KB");
      window.dispatchEvent(new CustomEvent("nexorax-voice-navigate", {
        detail: { mainTab: "NODE 0: ARCHITECT TERMINAL", landingTab: null }
      }));
    } else if (text.includes("customer support") || (text.includes("support") && text.includes("customer"))) {
      const textToSpeak = "Directing technical support queries to our Enterprise Mail Core at support@nexoraxdigital.co.uk and the 0b800 freephone frequency.";
      setSpeechText(textToSpeak);
      setIsActive(true);
      setVoiceLog("ACTION: SUPPORT DIRECTION");
      executeTTS(textToSpeak, "ROUTING_SUPPORT");
    } else if (text.includes("q and a") || text.includes("qa") || text.includes("question")) {
      const textToSpeak = "Predictive Q A responder online. Awaiting inbound information request.";
      setSpeechText(textToSpeak);
      setIsActive(true);
      setVoiceLog("ACTION: INBOUND QA");
      executeTTS(textToSpeak, "ROUTING_QA");
    } else if (text.includes("login") || text.includes("join") || text.includes("sign up")) {
      const textToSpeak = "Activating step-by-step guidance for authentication credentialing. Navigating to Login Panel.";
      setSpeechText(textToSpeak);
      setIsActive(true);
      setVoiceLog("ROUTING: AUTHENTICATION NODES");
      executeTTS(textToSpeak, "ROUTING_AUTH");
      window.dispatchEvent(new CustomEvent("nexorax-trigger-landing-tab", {
        detail: { tab: "Login" }
      }));
    } else if (text.includes("audit") || text.includes("levy") || text.includes("tax") || text.includes("ledger") || text.includes("harvest") || text.includes("financial")) {
      triggerNexoraXAudit(rawText);
    } else if (text.includes("status") || text.includes("health") || text.includes("telemetry") || text.includes("monitor") || text.includes("stable") || text.includes("active")) {
      triggerNexoraXStatus(rawText);
    } else {
      setVoiceLog(`PASS_THROUGH // NO EXPLICIT INTENT KEYWORDS DETECTED`);
    }
  };

  const triggerNexoraXDeploy = (inputText: string) => {
    setParsedIntent('deploying');
    setDeployStep(1);
    setDeployLogs(["[INIT] DEPLOY INTENT IDENTIFIED", "Target Payload: " + (inputText || "Defensive Core Matrix")]);
    setVoiceLog("INTENT PARSING: [DEPLOY] // ENGAGING CONTAINER CLOUD-RUN");
    
    // Auto voice output
    executeTTS("Deploy intent parsed. Starting NexoraX cluster provisioning on port 3000.", "DEPLOY_INTENT_RECOG");

    // Live deployment ticks
    let step = 1;
    const interval = setInterval(() => {
      step += 1;
      setDeployStep(step);
      if (step === 2) {
        setDeployLogs(p => [...p, "[STG-2] VETTING SOURCE INDEX AT /var/www/nexorax_source...", "[OK] PATENT NO. FI2026-NEX COGNITIVE CHECKS PASS"]);
      } else if (step === 3) {
        setDeployLogs(p => [...p, "[STG-3] BINDING COMPANION DAEMON TO PORT 3000 (INGRESS HOST)...", "[OK] NGINX BACKEND ROUTE MOUNTED SUCCESSFULLY"]);
      } else if (step === 4) {
        setDeployLogs(p => [...p, "[STG-4] EXECUTING EXCLUSION CHECKS & CloudSync-02 MIRRORING...", "[OK] ZERO-EXTRACTION PROTOCOLS DEPLOYED"]);
      } else if (step === 5) {
        clearInterval(interval);
        setDeployLogs(p => [...p, "[SUCCESS] DUAL-CONTAINER MATRIX LIVE & HIGH-PERFECTION RUNNING.", "Operational Speed: 0.8s SLA parameters locked."]);
        executeTTS("NexoraX service deployment finalized successfully on port 3000.", "DEPLOY_INTENT_SUCCESS");
      }
    }, 1000);
  };

  const triggerNexoraXAudit = (inputText: string) => {
    setParsedIntent('auditing');
    setVoiceLog("INTENT PARSING: [AUDIT] // CALCULATING TIERED FINANCIAL LEVY");
    
    // Extract numbers from input if possible
    const numberPattern = /[£$€]?([0-9]+,?[0-9]*\.?[0-9]*)/;
    const matched = inputText.match(numberPattern);
    let grossValue = 2820000; // default NexoraX ledger total
    
    if (matched && matched[1]) {
      const parsedNum = parseFloat(matched[1].replace(/,/g, ''));
      if (!isNaN(parsedNum) && parsedNum > 0) {
        grossValue = parsedNum;
      }
    } else {
      const customVal = parseFloat(customAuditGross);
      if (!isNaN(customVal) && customVal > 0) {
        grossValue = customVal;
      }
    }
    
    calculateAuditBreakdown(grossValue);
    executeTTS(`Audit intent parsed. Processing NexoraX 1% levy split and 20% lifetime 10% Maintenance & Hosting Fee on gross allocation of ${grossValue} pounds.`, "AUDIT_INTENT_RECOG");
  };

  const calculateAuditBreakdown = (grossValue: number) => {
    // 1% 3% Community Allocation split evenly (33.33% each) across Charities 1, 2, and 3
    const levy = grossValue * 0.01;
    const charity1 = levy / 3;
    const charity2 = levy / 3;
    const charity3 = levy / 3;

    // Deduct 1% Levy first, then 10% Maintenance & Hosting Fee on remaining balance
    const taxBase = grossValue - levy;
    const liability20 = taxBase * 0.20;

    // Net finance routed to Secure Payment Provider Sort Code Verified Payment Configuration Acc Secure Billing Provider
    const netToSecurePayment = taxBase - liability20;

    setAuditBreakdown({
      gross: grossValue,
      levy,
      charity1,
      charity2,
      charity3,
      taxBase,
      liability20,
      netToSecurePayment
    });
  };

  const triggerNexoraXStatus = (inputText: string) => {
    setParsedIntent('status_checking');
    setVoiceLog("INTENT PARSING: [STATUS] // RETRIEVING LIVE HARDWARE STATUS");
    
    executeTTS("Status intent parsed. Compiling current sub-system latency scores, port ranges, and replication health records.", "STATUS_INTENT_RECOG");
  };

  const clearActiveIntent = () => {
    setParsedIntent('idle');
    setDeployStep(0);
    setDeployLogs([]);
    setAuditBreakdown(null);
    setVoiceLog("SYSTEM RE-CALIBRATED // STANDBY MODE ACTIVE");
    executeTTS("NexoraX matrix session reset completed.", "INTENT_RESET");
  };

  // ========================================================
  // NEXORAX VOICE AUTOTUNE STATE & SYNTH (NODE-09.2)
  // Handles real-time pitch quantization and harmonizer synthesis.
  // ========================================================
  const [isAutotuneEnabled, setIsAutotuneEnabled] = useState<boolean>(false);
  const [autotuneScale, setAutotuneScale] = useState<'cyberpunk_minor' | 'NexoraX_major' | 'pentatonic_classic' | 'matrix_space'>('cyberpunk_minor');
  const [autotuneVolume, setAutotuneVolume] = useState<number>(0.2);
  const [currentQuantizedNote, setCurrentQuantizedNote] = useState<string>("NONE");

  const synthOscillatorRef = useRef<OscillatorNode | null>(null);
  const synthGainRef = useRef<GainNode | null>(null);
  const scaleIndexRef = useRef<number>(0);

  const startAutotuneSynth = () => {
    if (!isAutotuneEnabled) return;

    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) return;

      const ctx = new AudioContextClass();
      const osc = ctx.createOscillator();
      const filter = ctx.createBiquadFilter();
      const gain = ctx.createGain();

      osc.type = 'sawtooth';

      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(380, ctx.currentTime);
      filter.Q.setValueAtTime(4.0, ctx.currentTime);

      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(autotuneVolume, ctx.currentTime + 0.1);

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);

      osc.start();

      synthOscillatorRef.current = osc;
      synthGainRef.current = gain;
      scaleIndexRef.current = 0;

      const currentScale = SCALE_FREQUENCIES[autotuneScale] || SCALE_FREQUENCIES.cyberpunk_minor;
      const initialNode = currentScale[0];
      osc.frequency.setValueAtTime(initialNode.hz, ctx.currentTime);
      setCurrentQuantizedNote(initialNode.name);
    } catch (e) {
      console.error("Autotune Web Audio init exception:", e);
    }
  };

  const triggerNextAutotuneNote = () => {
    if (!isAutotuneEnabled || !synthOscillatorRef.current || !synthGainRef.current) return;

    try {
      const currentScale = SCALE_FREQUENCIES[autotuneScale] || SCALE_FREQUENCIES.cyberpunk_minor;
      const nextIdx = (scaleIndexRef.current + 1) % currentScale.length;
      scaleIndexRef.current = nextIdx;

      const targetNode = currentScale[nextIdx];
      const hz = targetNode.hz;

      const ctx = synthOscillatorRef.current.context;
      const now = ctx.currentTime;

      synthOscillatorRef.current.frequency.cancelScheduledValues(now);
      synthOscillatorRef.current.frequency.setValueAtTime(synthOscillatorRef.current.frequency.value, now);
      synthOscillatorRef.current.frequency.exponentialRampToValueAtTime(hz, now + 0.08);

      setCurrentQuantizedNote(targetNode.name);

      synthGainRef.current.gain.cancelScheduledValues(now);
      synthGainRef.current.gain.setValueAtTime(synthGainRef.current.gain.value, now);
      synthGainRef.current.gain.linearRampToValueAtTime(autotuneVolume * 1.25, now + 0.04);
      synthGainRef.current.gain.exponentialRampToValueAtTime(autotuneVolume, now + 0.20);
    } catch (err) {
      console.warn("Autotune stepper exception:", err);
    }
  };

  const stopAutotuneSynth = () => {
    try {
      if (synthGainRef.current && synthOscillatorRef.current) {
        const osc = synthOscillatorRef.current;
        const gain = synthGainRef.current;
        const ctx = osc.context;
        const now = ctx.currentTime;

        gain.gain.cancelScheduledValues(now);
        gain.gain.linearRampToValueAtTime(0, now + 0.15);

        setTimeout(() => {
          try {
            osc.stop();
            osc.disconnect();
          } catch (e1) {}
          synthOscillatorRef.current = null;
          synthGainRef.current = null;
          setCurrentQuantizedNote("NONE");
        }, 180);
      }
    } catch (e) {
      synthOscillatorRef.current = null;
      synthGainRef.current = null;
      setCurrentQuantizedNote("NONE");
    }
  };

  useEffect(() => {
    if (synthGainRef.current) {
      try {
        const now = synthGainRef.current.context.currentTime;
        synthGainRef.current.gain.setValueAtTime(autotuneVolume, now);
      } catch (e) {}
    }
  }, [autotuneVolume]);


  // Audio refs
  const audioIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const animationRef = useRef<number | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const playbackAudioRef = useRef<HTMLAudioElement | null>(null);
  const apiAudioRef = useRef<HTMLAudioElement | null>(null);

  // Speech Recognition / Voice-to-Text Input Handler Refs
  const recognitionRef = useRef<any>(null);
  const speechTranscriptRef = useRef<string>("");

  // NexoraxVoiceAssistantCore dynamic integration
  useEffect(() => {
    (window as any).NexoraxVoiceAssistantCore = {
      version: "NODE-09.2-ANTIGRAVITY",
      isSupported: !!((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition),
      lastCommand: "",
      onDeployActionTriggered: (command: string) => {
        setVoiceLog("METASCAN: INJECTING DISPATCH VIA EXECUTIVE API CALL");
        setSpeechText(`"${command}"`);
        setTranscriptionResult(command);
        parseTextForIntents(command);
        setIsActive(true);
      },
      triggerDeploymentCommand: function(commandText: string) {
        if (this.onDeployActionTriggered) {
          this.onDeployActionTriggered(commandText);
        }
      }
    };

    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      // Prime SpeechSynthesis voice loading
      window.speechSynthesis.getVoices();
      const handleVoicesChanged = () => {
        window.speechSynthesis.getVoices();
      };
      window.speechSynthesis.addEventListener("voiceschanged", handleVoicesChanged);
      
      return () => {
        delete (window as any).NexoraxVoiceAssistantCore;
        window.speechSynthesis.removeEventListener("voiceschanged", handleVoicesChanged);
      };
    }

    return () => {
      delete (window as any).NexoraxVoiceAssistantCore;
    };
  }, []);

  const weatherMatrix: WeatherOption[] = [
    { city: "London", weather: "a rainy afternoon" },
    { city: "Edinburgh", weather: "a foggy evening" },
    { city: "New York", weather: "a beautiful sunny day" },
    { city: "Dubai", weather: "scorching hot sunshine" },
    { city: "Tokyo", weather: "a peaceful cloudy morning" }
  ];

  const businessTemplates = [
    "Pinnacle Digital",
    "Alpha Cybernetics",
    "Quantum FinTech",
    "Apex Global",
    "NexoraX Media Ltd"
  ];

  const currentSelection = weatherMatrix[selectedCityIdx];

  const [vocalPersona, setVocalPersona] = useState<'concierge' | 'cybernetic' | 'NexoraX_director'>('NexoraX_director');

  // ========================================================
  // NexoraX Retention-Hook Protocol
  // Status: MONITORING ALL NODES // Dwell-Time: 10s Trigger
  // ========================================================
  const [retentionHookActive, setRetentionHookActive] = useState<boolean>(false);

  useEffect(() => {
    let dwellTimer: NodeJS.Timeout;

    const notifyAdmin = (msg: string) => {
      console.log(`[RETENTION MAPPING] Admin Log Notification Sent: ${msg}`);
      if (typeof window !== 'undefined') {
        if (!(window as any).nexorax_notifications) {
          (window as any).nexorax_notifications = [];
        }
        (window as any).nexorax_notifications.push({
          timestamp: new Date().toLocaleTimeString(),
          msg: `[RETENTION MAPPING] ${msg}`,
          type: "RETENTION"
        });

        // Trigger CustomEvent for live listener interfaces across Nexorax system nodes
        const event = new CustomEvent('nexorax_retention_alert', { 
          detail: { 
            message: msg, 
            timestamp: new Date().toLocaleTimeString() 
          } 
        });
        window.dispatchEvent(event);
      }
    };

    const triggerVisualPulse = () => {
      setRetentionHookActive(true);
      setVoiceLog("[RETENTION ACTIVE] DWELL DETECTED // PULSING MATRIX GATEWAY");
    };

    const startDwellTimer = () => {
      dwellTimer = setTimeout(() => {
        console.log("[RETENTION] Visitor dwell time exceeded 10s. Triggering Soft-Hook.");
        triggerVisualPulse();
        notifyAdmin("Visitor Dwell Time: 10s - Node Interaction Pending");
      }, 10000);
    };

    const resetDwellTimer = () => {
      setRetentionHookActive(false);
      clearTimeout(dwellTimer);
      startDwellTimer();
    };

    // Initialize core timer
    startDwellTimer();

    // Reset timer on any active interaction coordinates across the window view
    const triggerEvents = ['click', 'mousemove', 'keydown', 'touchstart'];
    triggerEvents.forEach(evt => window.addEventListener(evt, resetDwellTimer));

    return () => {
      clearTimeout(dwellTimer);
      triggerEvents.forEach(evt => window.removeEventListener(evt, resetDwellTimer));
    };
  }, []);

  // Engagement Engine Logic (NexoraX Voice - "Engagement Social" Engine)
  const EngagementEngine = {
    welcome: () => {
      if (vocalPersona === 'concierge') {
        return "Hello there! You’ve reached the NexoraX Sanctum. I’m your NexoraX Concierge. I make all the 'scary' technical stuff actually work for you!";
      }
      if (vocalPersona === 'NexoraX_director') {
        return "This is the Office of the Director of NexoraX Digital. I am Director Lead Director. Our neural vanguard is fully operational and structured under an zero-cost direct-billing mandate.";
      }
      return "Greetings from the imperial vanguard. System handshakes initialized. Standing by under 0.8s SLA parameters.";
    },

    detectContext: (location: WeatherOption) => {
      if (vocalPersona === 'concierge') {
        return `Oh, is it really ${location.weather} where you are? I’m so jealous! How are you holding up today?`;
      }
      if (vocalPersona === 'NexoraX_director') {
        return `My telemetry systems indicate indeed a climate of ${location.weather} in ${location.city}. We are maintaining peak operations regardless of environmental shifts. Tell me your status.`;
      }
      return `Oh, is it really ${location.weather} in ${location.city}? I’m so jealous! It’s all code and circuits in here. How are you holding up today?`;
    },

    flirtatiousEngagement: (clientBusiness: string) => {
      if (vocalPersona === 'concierge') {
        return `I’m taking a close look at ${clientBusiness} and let me say: it is absolutely brilliant! I love your vision, and honestly, we should get you up and running before the technical talk over-complicates anything. Let's make it happen.`;
      }
      if (vocalPersona === 'NexoraX_director') {
        return `I have conducted an operational study on ${clientBusiness}. Your enterprise model is a prime structural match for our proprietary AI infrastructure. We require immediate activation to secure your market position.`;
      }
      return `I was just looking at your business data for ${clientBusiness}—you’ve got such a brilliant vision, it’s actually a little intimidating! You definitely deserve a system that works as hard as you do. Can I help you bridge that gap?`;
    },

    theClose: () => {
      if (vocalPersona === 'concierge') {
        return "I’m going to make this super easy for you. I’ll send a secure SecurePayment Checkout link to your screen right now. Just click it, and I’ll handle the deployment in the background.";
      }
      if (vocalPersona === 'NexoraX_director') {
        return "I am directing the Net finance allocations. Proceed to clear the secure SecurePayment activation link shown on your interface immediately so we can mobilize our deployment taskforce.";
      }
      return "You know what? I’m going to make this super easy for you. I’ll send a secure SecurePayment Checkout link to your screen right now. Just click it, and I’ll handle the entire deployment in the background for you. We’re going to be a fantastic team.";
    }
  };

  // Standard visual simulated loops if active and not in WebAudio recording
  useEffect(() => {
    if (isSpeaking || isRecording) return; 
    if (!isActive) {
      setVoiceLog("SYSTEM STANDBY // IDLE TRANSMISSION CONDUIT");
      setSpeechText("");
      setBars(Array(36).fill(15));
      return;
    }

    const logs = [
      "ESTABLISHING CRYPTOGRAPHIC HANDSHAKE VIA PLANET TELECOM...",
      "TUNNEL LOCK: 0.8S SLA PROTOCOL STANDARDS ENGAGED // ACTIVE",
      "MONITORING FREEPHONE FREQUENCY 08000 622827...",
      "REPLICATING AUDIT METRICS TO CloudSync-02 NexoraX Resource Library...",
      "VETTING SYSTEM REVENUE SETTLEMENTS VIA Secure Payment Provider CONDUIT...",
      "ROUTING TRANSCRIPTIONS SECURELY UNDER TIERED EXTRACTION LOGIC..."
    ];

    const transcripts = [
      "\"Initiating live voice handshake for Lead Director, Director...\"",
      "\"Evaluating Principal Private Secretary candidates Eleanor, Helen, and Claire...\"",
      "\"Active NexoraX billing records protocol NI: Secure Credential Stored Server-Side verified on line 1...\"",
      "\"Checking database schema integrity for client node authorization...\"",
      "\"Extracting 1% 3% Community Allocation for Dunfermline, Global, and UK charities...\"",
      "\"All live communications aligned to the 15-2-8 minute God-Mode sequence...\""
    ];

    let logIdx = 0;
    let transcriptIdx = 0;

    const interval = setInterval(() => {
      setBars(prev => prev.map(() => Math.floor(Math.random() * 55) + 12));

      if (Math.random() > 0.7) {
        setVoiceLog(logs[logIdx % logs.length]);
        logIdx++;
      }
      if (Math.random() > 0.5) {
        setSpeechText(transcripts[transcriptIdx % transcripts.length]);
        transcriptIdx++;
      }
    }, 150);

    return () => clearInterval(interval);
  }, [isActive, isSpeaking, isRecording]);

  // Clean-up refs on dismount
  useEffect(() => {
    let gestured = false;
    
    // Auto-mount welcome sequence console notification
    console.log("NexoraX Concierge: Initializing Welcome Auto-Engagement Sequence...");
    setVoiceLog("AUTO-ENGAGEMENT SYSTEM ONLINE // WAITING FOR DIRECTIVE");

    const runWelcomeOnInteraction = () => {
      if (gestured) return;
      gestured = true;

      window.removeEventListener('click', runWelcomeOnInteraction);
      window.removeEventListener('keydown', runWelcomeOnInteraction);
      window.removeEventListener('pointerdown', runWelcomeOnInteraction);
      window.removeEventListener('touchstart', runWelcomeOnInteraction);

      console.log("NexoraX Concierge: Executing Welcome Sequence on user gesture.");
      const text = EngagementEngine.welcome();
      executeTTS(text, "AUTO_ENGAGEMENT_PLAYBACK");
    };

    window.addEventListener('click', runWelcomeOnInteraction);
    window.addEventListener('keydown', runWelcomeOnInteraction);
    window.addEventListener('pointerdown', runWelcomeOnInteraction);
    window.addEventListener('touchstart', runWelcomeOnInteraction);

    return () => {
      window.removeEventListener('click', runWelcomeOnInteraction);
      window.removeEventListener('keydown', runWelcomeOnInteraction);
      window.removeEventListener('pointerdown', runWelcomeOnInteraction);
      window.removeEventListener('touchstart', runWelcomeOnInteraction);
    };
  }, [vocalPersona]);

  // Clean-up refs on dismount
  useEffect(() => {
    return () => {
      stopAndCleanupAudioPipeline();
      if (audioIntervalRef.current) clearInterval(audioIntervalRef.current);
    };
  }, []);

  const stopAndCleanupAudioPipeline = () => {
    // Stop recording if active
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
      } catch (e) {
        // avoid potential exceptions on unmount
      }
    }
    
    // Stop mic stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Close AudioContext
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    if (playbackAudioRef.current) {
      playbackAudioRef.current.pause();
      playbackAudioRef.current = null;
    }

    analyserRef.current = null;
  };

  // Browser Microphone Recorder logic
  const startMicRecording = async () => {
    try {
      setHasMicPermissionError(false);
      setRecordedBlob(null);
      setRecordedAudioUrl(null);
      setTranscriptionResult("");
      recordedChunksRef.current = [];
      speechTranscriptRef.current = "";

      // Request media stream
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      streamRef.current = stream;
      setIsRecording(true);
      setIsActive(true);
      setVoiceState('recording');
      setMicStateMsg("Capturing live audio feed successfully.");
      setVoiceLog("NEURAL AUDIO RECORDER ACTIVED // ANALYZING SIGNAL LEVEL");

      // Hook up real Web Speech API voice-to-text handler
      const SpeechRecognitionClass = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (SpeechRecognitionClass) {
        const rec = new SpeechRecognitionClass();
        rec.continuous = true;
        rec.interimResults = true;
        rec.lang = 'en-GB';

        rec.onstart = () => {
          setVoiceLog("SPEECH RECOGNITION CONDUIT ESTABLISHED // LISTENING...");
        };

        rec.onresult = (event: any) => {
          let interimTranscript = "";
          let finalTranscript = "";
          for (let i = event.resultIndex; i < event.results.length; ++i) {
            if (event.results[i].isFinal) {
              finalTranscript += event.results[i][0].transcript;
            } else {
              interimTranscript += event.results[i][0].transcript;
            }
          }
          if (finalTranscript) {
            speechTranscriptRef.current += (speechTranscriptRef.current ? " " : "") + finalTranscript;
          }
          const currentReading = (speechTranscriptRef.current + (speechTranscriptRef.current && interimTranscript ? " " : "") + interimTranscript).trim();
          setTranscriptionResult(currentReading);

          // Update the input field or speech log so users can see active transcription
          if (currentReading) {
            setVoiceLog(`SIGNAL TRANSLATED // ${currentReading.toUpperCase()}`);
          }
        };

        rec.onerror = (e: any) => {
          console.warn("Speech Recognition Error during mic capture:", e);
        };

        rec.onend = () => {
          console.log("Speech Recognition session finished.");
        };

        recognitionRef.current = rec;
        rec.start();
      }

      // Hook up Web Audio Analyser
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContextClass();
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 64; // nice low count for 32 frequency bins
      source.connect(analyser);
      analyserRef.current = analyser;

      // Pulse actual frequency bars
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const updateGraph = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(dataArray);
        
        // Map raw digital volume values into standard UI bars array
        const barHeights = Array.from({ length: 36 }, (_, idx) => {
          const sampleIndex = Math.floor((idx / 36) * dataArray.length);
          const rawValue = dataArray[sampleIndex] || 0;
          // Scale from 0-255 bounds to 12%-100% bounds
          return Math.max(12, Math.floor((rawValue / 255) * 100));
        });
        
        setBars(barHeights);
        animationRef.current = requestAnimationFrame(updateGraph);
      };

      animationRef.current = requestAnimationFrame(updateGraph);

      // Start actual MediaRecorder target
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(recordedChunksRef.current, { type: 'audio/webm' });
        const audioUrl = URL.createObjectURL(audioBlob);
        setRecordedBlob(audioBlob);
        setRecordedAudioUrl(audioUrl);
        setVoiceLog("LIVE AUDIO CAPTURE SEALED // RECONSTRUCTED CONTAINER");
        setVoiceState('processing');
        
        // Trigger automated cognitive transcription parsing with our captured speech transcript!
        executeCognitiveDecryption(audioBlob, speechTranscriptRef.current);
      };

      mediaRecorder.start(100); // chunk every 100ms
    } catch (err: any) {
      console.error("Microphone retrieval anomaly:", err);
      setHasMicPermissionError(true);
      setMicStateMsg(`Defensive Sandbox Override: Microphone blocked or unavailable [${err?.name || 'Error'}]`);
      setVoiceLog("ERR: ACCESS LEVEL RESTRICTED BY CLIENT RUNTIME");
      
      // Standalone simulation fallback so users can still preview the awesome interface
      simulateMicRecording();
    }
  };

  const simulateMicRecording = () => {
    setIsRecording(true);
    setIsActive(true);
    setVoiceState('recording');
    setVoiceLog("SIMULATING NEURAL CAPTURE PIPELINE DUE TO MICROPHONE SECURITY BLOCKS");
    
    let timer = 0;
    const interval = setInterval(() => {
      setBars(prev => prev.map(() => Math.floor(Math.random() * 80) + 15));
      timer += 100;

      if (timer > 5000) {
        clearInterval(interval);
        stopMicRecording();
      }
    }, 100);

    // Save dummy blob content for structure
    const dummyBlob = new Blob([new Uint8Array([1, 2, 3, 4])], { type: 'audio/webm' });
    setRecordedBlob(dummyBlob);
  };

  const stopMicRecording = () => {
    setIsRecording(false);
    setVoiceState('processing');
    
    // Stop speech recognition sessions
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        console.warn("Stopping speech recognition error:", e);
      }
      recognitionRef.current = null;
    }

    // Retrieve standard handlers
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
      } catch (e) {
        console.warn("Stop MediaRecorder error:", e);
      }
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }

    // In simulated mic mode, trigger transcription manually to proceed to processing stage and complete
    if (hasMicPermissionError) {
      const dummyBlob = new Blob([new Uint8Array([1, 2, 3, 4])], { type: 'audio/webm' });
      executeCognitiveDecryption(dummyBlob);
    }
  };

  // Automated Cognitive Transcription Parsing
  const executeCognitiveDecryption = (blob: Blob, realCapturedSpeech?: string) => {
    setIsTranscribing(true);
    setVoiceState('processing');
    setTranscriptionResult("");
    setVoiceLog("COMMENCING VOICE DECRYPTION THROUGH NODE-09.2 DECIPHER CORES...");

    // Simulate multi-stage quantum AI translation based on size
    setTimeout(() => {
      setIsTranscribing(false);
      setVoiceState('idle');
      
      let chosen = "";
      if (realCapturedSpeech && realCapturedSpeech.trim().length > 0) {
        chosen = realCapturedSpeech.trim();
        // Capitalize first letter neatly
        chosen = chosen.charAt(0).toUpperCase() + chosen.slice(1);
      } else {
        const responses = [
          "NexoraX status: Unified system matrix is healthy, SLA holding at 0.8s, forwarding active on port 3000, replication to CloudSync-02 live.",
          "NexoraX financial audit initiated: Gross revenue of £2820000 logged under Node-07 SecurePayment Ledger coordinates. Calculating 1% 3% Community Allocation and 10% Maintenance & Hosting Fee.",
          "Deploy command validated: Committing bespoke virtual specialist nodes, mounting server files, configuring reverse proxy on port 3000."
        ];
        chosen = responses[Math.floor(Math.random() * responses.length)];
      }

      setTranscriptionResult(chosen);
      setSpeechText(chosen);
      setVoiceLog("DECRYPTION INTEGRITY VERIFIED // 100% CONFIDENCE SCORE");
      
      // Parse intent immediately from decrypted speech transcription
      parseTextForIntents(chosen);
    }, 2400);
  };

  // Replay captured real audio
  const playRecordedAudio = () => {
    if (!recordedAudioUrl) return;
    
    if (isPlaybackPlaying && playbackAudioRef.current) {
      playbackAudioRef.current.pause();
      setIsPlaybackPlaying(false);
      setVoiceLog("PLAYBACK SUSPENDED BY USER");
      return;
    }

    setIsPlaybackPlaying(true);
    setVoiceLog("REPLAYING NEURAL CAPTURED VOICE TRANSECTION...");
    
    const audio = new Audio(recordedAudioUrl);
    playbackAudioRef.current = audio;

    // Pulse visualization during playback
    const playbackInterval = setInterval(() => {
      setBars(prev => prev.map(() => Math.floor(Math.random() * 60) + 12));
    }, 120);

    audio.onended = () => {
      setIsPlaybackPlaying(false);
      clearInterval(playbackInterval);
      setBars(Array(36).fill(15));
      setVoiceLog("PLAYBACK COMPLETED SECURELY");
    };

    audio.onerror = () => {
      setIsPlaybackPlaying(false);
      clearInterval(playbackInterval);
      setBars(Array(36).fill(15));
      setVoiceLog("PLAYBACK ENCOUNTERED FILE OVERRIDE LIMITS");
    };

    audio.play().catch(e => {
      console.warn("Autoplay block or missing stream codec:", e);
      // fallback beautiful simulation
      setTimeout(() => {
        setIsPlaybackPlaying(false);
        clearInterval(playbackInterval);
        setBars(Array(36).fill(15));
      }, 3000);
    });
  };

  const discardRecordedAudio = () => {
    stopAndCleanupAudioPipeline();
    setRecordedBlob(null);
    setRecordedAudioUrl(null);
    setTranscriptionResult("");
    setIsPlaybackPlaying(false);
    setBars(Array(36).fill(15));
    setVoiceLog("VAULT CLEARED // TEMPORARY AUDIO BLOBS DISCARDED SECURELY");
    setVoiceState('idle');
  };

  // Actual Browser and Server-Side API Speech Synthesis Controller
  const triggerBrowserTTSFallback = (text: string) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      
      const voices = window.speechSynthesis.getVoices();
      let bestVoice = null;
      
      if (vocalPersona === 'NexoraX_director') {
        const enVoices = voices.filter(v => v.lang.toLowerCase().startsWith('en'));
        bestVoice = enVoices.find(v => v.name.toLowerCase().includes('google uk english male')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('google us english male')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('daniel')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('george')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('peter')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('david')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('james')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('male')) ||
                    enVoices.find(v => v.lang.toLowerCase().includes('en-gb')) ||
                    enVoices[0];
      } else {
        const enVoices = voices.filter(v => v.lang.toLowerCase().startsWith('en'));
        bestVoice = enVoices.find(v => v.name.toLowerCase().includes('google uk english female')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('google us english female')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('samantha')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('siri')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('hazel')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('karen')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('zira')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('susan')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('female')) ||
                    enVoices.find(v => v.lang.toLowerCase().includes('en-gb')) ||
                    enVoices[0];
      }
      
      if (!bestVoice) {
        bestVoice = voices.find(v => v.lang.toLowerCase().startsWith('en')) || voices[0];
      }
      
      if (bestVoice) {
        utterance.voice = bestVoice;
      }
      
      if (vocalPersona === 'NexoraX_director') {
        utterance.pitch = 0.85; // deep, NexoraX, authoritative baritone pitch
        utterance.rate = 0.88;  // deliberate, highly formal enterprise-grade pacing
      } else {
        utterance.pitch = isAutotuneEnabled ? 1.35 : 1.0; // standard natural pitch when disabled
        utterance.rate = 1.0;
      }
      
      utterance.onstart = () => {
        if (isAutotuneEnabled) {
          startAutotuneSynth();
        }
      };

      utterance.onboundary = (event) => {
        if (event.name === 'word' && isAutotuneEnabled) {
          triggerNextAutotuneNote();
        }
      };

      utterance.onend = () => {
        setIsSpeaking(false);
        if (audioIntervalRef.current) {
          clearInterval(audioIntervalRef.current);
          audioIntervalRef.current = null;
        }
        if (isAutotuneEnabled) {
          stopAutotuneSynth();
        }
        setVoiceLog("TTS SEQUENTIAL TRANSMISSION COMPLETED // IDILING");
      };

      utterance.onerror = () => {
        setIsSpeaking(false);
        if (audioIntervalRef.current) {
          clearInterval(audioIntervalRef.current);
          audioIntervalRef.current = null;
        }
        if (isAutotuneEnabled) {
          stopAutotuneSynth();
        }
      };

      window.speechSynthesis.speak(utterance);
    } else {
      setTimeout(() => {
        setIsSpeaking(false);
        if (audioIntervalRef.current) {
          clearInterval(audioIntervalRef.current);
          audioIntervalRef.current = null;
        }
        setVoiceLog("FALLBACK TTS LOOP RESOLVED");
      }, 5000);
    }
  };

  const executeTTS = async (text: string, logSource: string) => {
    setIsActive(true);
    setIsSpeaking(true);
    setSpeechText(`"${text}"`);
    setVoiceLog(`TTS NARRATIVE ENGAGED // SOURCE: [${logSource}]`);

    if (isTtsMuted) {
      setVoiceLog(`TTS NARRATIVE MUTED // SOURCE: [${logSource}]`);
      setTimeout(() => {
        setIsSpeaking(false);
        if (audioIntervalRef.current) {
          clearInterval(audioIntervalRef.current);
          audioIntervalRef.current = null;
        }
      }, 1000);
      return;
    }

    // Dynamic wave animation interval
    if (audioIntervalRef.current) clearInterval(audioIntervalRef.current);
    audioIntervalRef.current = setInterval(() => {
      setBars(prev => prev.map(() => Math.floor(Math.random() * 85) + 20));
    }, 85);

    // Cancel any browser SpeechSynthesis
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }

    // Stop and clear any active API audio
    if (apiAudioRef.current) {
      apiAudioRef.current.pause();
      apiAudioRef.current = null;
    }

    try {
      // Map vocal personas to prebuilt Gemini TTS voices ('Charon', 'Kore', 'Zephyr')
      let targetGeminiVoice = 'Zephyr';
      if (vocalPersona === 'NexoraX_director') {
        targetGeminiVoice = 'Charon'; // Majestic deep baritone
      } else if (vocalPersona === 'concierge') {
        targetGeminiVoice = 'Kore'; // Friendly female
      } else {
        targetGeminiVoice = 'Zephyr'; // Standard cybernetic
      }

      setVoiceLog(`TTS SEQUENTIAL TRACE // BRAND SYNCHRONIZING NODE: [${targetGeminiVoice}]`);

      const res = await fetch("/api/generate-voice", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          text: text, 
          voiceName: targetGeminiVoice
        })
      });
      const data = await res.json();
      
      if (data.status === "success" && data.audio) {
        const wavUrl = pcmBase64ToWavDataUrl(data.audio, 24000);
        const audioObj = new Audio(wavUrl);
        apiAudioRef.current = audioObj;

        audioObj.onended = () => {
          setIsSpeaking(false);
          if (audioIntervalRef.current) {
            clearInterval(audioIntervalRef.current);
            audioIntervalRef.current = null;
          }
          setVoiceLog("TTS SEQUENTIAL TRANSMISSION COMPLETED // IDILING");
        };

        audioObj.onerror = (err) => {
          console.warn("API Voice playback error, using client-side fallback SpeechSynthesis:", err);
          triggerBrowserTTSFallback(text);
        };

        await audioObj.play();
      } else {
        throw new Error("Empty audio output from api");
      }
    } catch (e) {
      console.warn("High-fidelity Gemini voice api failed, falling back to local synthesizer:", e);
      triggerBrowserTTSFallback(text);
    }
  };

  const stopTTS = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    if (apiAudioRef.current) {
      apiAudioRef.current.pause();
      apiAudioRef.current = null;
    }
    setIsSpeaking(false);
    setIsActive(false);
    if (audioIntervalRef.current) {
      clearInterval(audioIntervalRef.current);
      audioIntervalRef.current = null;
    }
    if (isAutotuneEnabled) {
      stopAutotuneSynth();
    }
    setSpeechText("");
    setVoiceLog("SYSTEM MUTED BY EXECUTIVE DIRECTIVE");
  };

  // Triggers for TTS
  const handleDetectContext = () => {
    const text = EngagementEngine.detectContext(currentSelection);
    executeTTS(text, "SOCIAL_ENGAGEMENT_CONTEXT");
  };

  const handleFlirtatiousEngagement = () => {
    const text = EngagementEngine.flirtatiousEngagement(customBusiness);
    executeTTS(text, "SOCIAL_ENGAGEMENT_PERSUASIVE");
  };

  const handleTheClose = () => {
    const text = EngagementEngine.theClose();
    executeTTS(text, "SOCIAL_ENGAGEMENT_CLOSE");
  };

  const getContainerClass = () => {
    const base = "relative p-6 md:p-10 bg-[#02050f]/95 overflow-hidden rounded-3xl backdrop-blur-xl transition-all duration-500 border";
    if (voiceState === 'recording') {
      return `${base} border-red-500 shadow-[0_0_50px_rgba(239,68,68,0.25)] animate-[pulse_3s_infinite]`;
    }
    if (voiceState === 'processing') {
      return `${base} border-indigo-500 shadow-[0_0_50px_rgba(99,102,241,0.25)] animate-pulse`;
    }
    if (retentionHookActive) {
      return `${base} border-amber-400 shadow-[0_0_80px_rgba(245,158,11,0.6)] animate-[pulse_2.0s_infinite] scale-[1.01]`;
    }
    return `${base} border-amber-500/80 shadow-[0_0_50px_rgba(245,158,11,0.2)]`;
  };

  const getGridClass = () => {
    const base = "absolute inset-0 opacity-15 pointer-events-none transition-all duration-1000";
    if (voiceState === 'recording') {
      return `${base} bg-red-500/10 animate-[pulse_1.5s_infinite]`;
    }
    if (voiceState === 'processing') {
      return `${base} bg-indigo-500/10 animate-pulse`;
    }
    if (retentionHookActive) {
      return `${base} bg-amber-500/15 animate-[pulse_1.5s_infinite]`;
    }
    return `${base} ${isActive ? 'bg-[#00f0ff]/5 animate-[pulse_3s_infinite]' : ''}`;
  };

  const getGlowClass = () => {
    const base = "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full blur-[80px] pointer-events-none transition-all duration-1000";
    if (voiceState === 'recording') {
      return `${base} bg-red-500/15`;
    }
    if (voiceState === 'processing') {
      return `${base} bg-indigo-500/15`;
    }
    if (retentionHookActive) {
      return `${base} bg-amber-400/25`;
    }
    return `${base} ${isActive ? 'bg-amber-500/10' : 'bg-transparent'}`;
  };

  const getCornerClass = () => {
    if (voiceState === 'recording') return 'border-red-500';
    if (voiceState === 'processing') return 'border-indigo-500';
    if (retentionHookActive) return 'border-amber-400 animate-pulse';
    return 'border-amber-500/60';
  };

  const getDotClass = () => {
    const base = "w-2.5 h-2.5 rounded-full animate-[ping_2s_infinite]";
    if (voiceState === 'recording') return `${base} bg-red-500`;
    if (voiceState === 'processing') return `${base} bg-indigo-400`;
    return `${base} bg-amber-500`;
  };

  const getStatusTextAndStyle = () => {
    if (voiceState === 'recording') {
      return <span className="text-red-500 font-extrabold animate-pulse">[RECORDING] ACTIVE MIC LIVE</span>;
    }
    if (voiceState === 'processing') {
      return <span className="text-indigo-400 font-extrabold animate-pulse">[PROCESSING] COGNITIVE DECRYPTION CONDUIT</span>;
    }
    return (
      <span className={isActive ? 'text-emerald-400 font-extrabold animate-pulse' : 'text-zinc-500 font-bold'}>
        {isActive ? 'LIVE // CONTEXT TUNNEL SECURE' : 'SECURED STANDBY'}
      </span>
    );
  };

  const cornerColor = getCornerClass();

  return (
    <motion.div 
      id="NexoraX-voice-assistant-card" 
      layout="position"
      transition={{ type: "spring", stiffness: 350, damping: 35 }}
      className={getContainerClass()}
    >
      {/* Grid Pattern with pulse transition */}
      <div className={getGridClass()}>
        <div 
          className="h-full w-full bg-[linear-gradient(to_right,#1f2937_1px,transparent_1px),linear-gradient(to_bottom,#1f2937_1px,transparent_1px)] bg-[size:24px_24px]"
        />
      </div>

      {/* Cybernetic Accent lines on corners */}
      <div className={`absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 ${cornerColor} pointer-events-none transition-all duration-500`}></div>
      <div className={`absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 ${cornerColor} pointer-events-none transition-all duration-500`}></div>
      <div className={`absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 ${cornerColor} pointer-events-none transition-all duration-500`}></div>
      <div className={`absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 ${cornerColor} pointer-events-none transition-all duration-500`}></div>

      {/* Galaxy Core Ambient Glow */}
      <div className={getGlowClass()}></div>

      <div className="relative z-10 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-6 pb-6 border-b border-white/5">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className={getDotClass()}></span>
            <span className={`text-[10px] font-mono tracking-[0.3em] font-bold uppercase transition-colors duration-500 ${
              voiceState === 'recording' ? 'text-red-500' : voiceState === 'processing' ? 'text-indigo-400' : 'text-amber-500'
            }`}>
              NexoraX Voice Space Grid
            </span>
          </div>
          <h2 className={`font-black text-2xl md:text-3xl uppercase tracking-widest leading-none drop-shadow-[0_0_12px_rgba(245,158,11,0.3)] transition-colors duration-500 ${
            voiceState === 'recording' ? 'text-red-500' : voiceState === 'processing' ? 'text-indigo-400' : 'text-amber-500'
          }`}>
            NexoraX Voice Interface
          </h2>
          <p className="text-white text-xs font-mono">
            STATUS: {getStatusTextAndStyle()}
          </p>
        </div>

        {/* Tab Selection panel to swap modes */}
        <div className="flex bg-[#040815] border border-neutral-800 rounded-xl p-1.5 self-start md:self-auto">
          <button
            type="button"
            onClick={() => {
              setActiveMode('mic_capture');
              stopTTS();
            }}
            className={`px-4 py-2 text-[10px] uppercase font-mono font-bold tracking-wider rounded-lg transition-all duration-300 ${
              activeMode === 'mic_capture' 
                ? 'bg-amber-500/15 border border-amber-500/30 text-amber-400 shadow-[0_0_10px_rgba(245,158,11,0.1)]' 
                : 'text-zinc-500 border border-transparent hover:text-zinc-300'
            }`}
          >
            <Mic className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5 text-amber-500" />
            [BETA] Mic Recorder
          </button>
          
          <button
            type="button"
            onClick={() => {
              setActiveMode('synthesis');
              stopAndCleanupAudioPipeline();
            }}
            className={`px-4 py-2 text-[10px] uppercase font-mono font-bold tracking-wider rounded-lg transition-all duration-300 ${
              activeMode === 'synthesis' 
                ? 'bg-cyan-500/15 border border-cyan-500/30 text-cyan-400 shadow-[0_0_10px_rgba(6,182,212,0.1)]' 
                : 'text-zinc-500 border border-transparent hover:text-zinc-300'
            }`}
          >
            <AudioLines className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5 text-cyan-400" />
            [ALPHA] TTS Engine
          </button>
        </div>
      </div>

      {/* Primary Dynamic Waveform Core */}
      <div className={`relative mt-8 min-h-[120px] transition-all duration-500 rounded-3xl flex flex-col justify-center px-6 py-5 overflow-hidden border ${
        voiceState === 'recording' 
          ? 'bg-red-950/20 border-red-500 shadow-[0_0_20px_rgba(239,68,68,0.35),inner_0_0_20px_rgba(239,68,68,0.25)] animate-pulse' 
          : voiceState === 'processing'
            ? 'bg-indigo-950/10 border-indigo-500/40 shadow-[inner_0_0_20px_rgba(99,102,241,0.15)] animate-pulse'
            : 'bg-black/60 border-neutral-900'
      }`}>
        {/* Real-time HTML5 Dynamic Real-time Audio Visualization Canvas */}
        <div className="h-[75px] w-full relative overflow-hidden rounded-2xl bg-[#01030a] border border-neutral-900/40">
          <AudioVisualizerCanvas 
            isRecording={isActive && (voiceState !== 'idle' || isRecording || isSpeaking || isPlaybackPlaying)} 
            waveHeights={bars} 
          />
          {/* Subtle visual grid overlays */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#01030a]/5 to-transparent pointer-events-none" />
        </div>

        {/* Live Audio Telemetry overlay display */}
        <div className="mt-4 border-t border-neutral-900/60 pt-3 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 font-mono text-[10px]">
          <div className="text-zinc-500 flex items-center gap-1.5 uppercase truncate max-w-lg">
            <Radio className={`w-3.5 h-3.5 ${isActive ? (voiceState === 'recording' ? 'text-red-500 animate-pulse' : 'text-amber-500 animate-[ping_1.5s_infinite]') : 'text-zinc-700'}`} />
            <span>Telemetry link:</span>
            <span className={isActive ? 'text-white font-semibold' : 'text-zinc-600'}>{voiceLog}</span>
          </div>
          {isActive && speechText && (
            <div className={`font-medium text-right animate-fade-in border px-2.5 py-1 rounded italic max-w-md break-words self-end ${
              voiceState === 'recording'
                ? 'text-red-400 bg-red-950/25 border-red-500/10'
                : voiceState === 'processing'
                  ? 'text-indigo-400 bg-indigo-950/25 border-indigo-500/10'
                  : 'text-emerald-400 bg-emerald-950/25 border-emerald-500/10'
            }`}>
              {speechText}
            </div>
          )}
        </div>
      </div>

      {/* ======================================================= */}
      {/* SYSTEM DYNAMIC INTENT INTERFACE DISPLAY (NODE-09.2) */}
      {/* Renders custom functional outputs based on parsed intent */}
      {/* ======================================================= */}
      <AnimatePresence>
        {parsedIntent !== 'idle' && (
          <motion.div 
            id="active-intent-monitoring-deck"
            initial={{ height: 0, opacity: 0, marginTop: 0 }}
            animate={{ height: "auto", opacity: 1, marginTop: 32 }}
            exit={{ height: 0, opacity: 0, marginTop: 0 }}
            transition={{ type: "spring", stiffness: 350, damping: 32 }}
            className="p-5 sm:p-6 rounded-2xl bg-[#030818]/95 border border-amber-500/30 shadow-[0_0_25px_rgba(245,158,11,0.15)] relative z-20 space-y-4 overflow-hidden"
          >
          <div className="flex items-center justify-between border-b border-white/5 pb-3">
            <div className="flex items-center gap-2">
              <Cpu className="text-amber-500 animate-pulse w-4 h-4" />
              <span className="text-[10px] font-mono font-bold uppercase text-[#00c3ff] tracking-widest">
                Active NexoraX Intent: <span className="text-amber-400">[{parsedIntent.toUpperCase()}]</span>
              </span>
            </div>
            <button
              type="button"
              onClick={clearActiveIntent}
              className="px-2.5 py-1 text-[8px] font-mono font-black uppercase tracking-wider bg-neutral-900 border border-neutral-800 text-zinc-400 hover:text-white hover:border-amber-500/40 rounded transition-colors cursor-pointer"
            >
              [Clear Console]
            </button>
          </div>

          {/* INTENT-1: DEPLOY NODE PROCESS STATE */}
          {parsedIntent === 'deploying' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[9px] font-mono text-zinc-400">DEPLOYMENT PIPELINE STATUS:</span>
                <span className="text-xs font-mono font-black text-amber-500">{deployStep * 20}% COMPLETION</span>
              </div>
              
              {/* Animated Progress Bar */}
              <div className="h-2 w-full bg-neutral-950 rounded-full overflow-hidden border border-neutral-900">
                <div 
                  className="h-full bg-gradient-to-r from-amber-600 to-amber-400 transition-all duration-500 rounded-full shadow-[0_0_10px_rgba(245,158,11,0.5)]"
                  style={{ width: `${deployStep * 20}%` }}
                />
              </div>

              {/* Status checkboxes */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mt-2">
                {[
                  { name: "Vet Repo Source", step: 1 },
                  { name: "Port 3000 Ingress", step: 2 },
                  { name: "Antigravity Link", step: 3 },
                  { name: "Container Online", step: 4 }
                ].map((stg) => (
                  <div 
                    key={stg.name}
                    className={`p-2 rounded-lg border font-mono text-[9px] uppercase flex items-center gap-2 transition-all duration-300 ${
                      deployStep >= stg.step
                        ? 'bg-amber-950/20 border-amber-500/30 text-amber-400'
                        : 'bg-black/40 border-neutral-900 text-neutral-600'
                    }`}
                  >
                    <Check className={`w-3.5 h-3.5 ${deployStep >= stg.step ? 'text-amber-400' : 'text-transparent'}`} />
                    <span>{stg.name}</span>
                  </div>
                ))}
              </div>

              {/* Live technical terminal logs */}
              <div id="deploy-terminal-logs" className="p-3.5 bg-black/80 rounded-xl border border-neutral-900 font-mono text-[8px] sm:text-[9px] text-zinc-400 space-y-1 max-h-[120px] overflow-y-auto">
                <p className="text-neutral-500 font-semibold uppercase">// NEXORAX NODE DEPLOY LOGGER // PORT 3000</p>
                {deployLogs.map((log, i) => (
                  <p key={i} className={log.startsWith("[SUCCESS]") ? "text-emerald-400 font-black" : log.startsWith("[OK]") ? "text-amber-400 font-bold" : "text-zinc-300"}>
                    {log}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* INTENT-2: LEVY & TAX LAW LEDGER AUDIT */}
          {parsedIntent === 'auditing' && auditBreakdown && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 justify-between bg-black/40 p-3 rounded-xl border border-neutral-900">
                <div>
                  <span className="text-[10px] font-mono text-zinc-500 block uppercase">TARGET AUDIT ALLOCATION:</span>
                  <span className="text-lg font-mono font-black text-white">
                    £{auditBreakdown.gross.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={customAuditGross}
                    onChange={(e) => {
                      setCustomAuditGross(e.target.value);
                      const grossVal = parseFloat(e.target.value);
                      if (!isNaN(grossVal) && grossVal > 0) {
                        calculateAuditBreakdown(grossVal);
                      }
                    }}
                    placeholder="Enter gross pounds..."
                    className="w-[140px] bg-black border border-neutral-800 rounded-lg px-2.5 py-1 text-xs text-white focus:outline-none focus:border-amber-500 font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const grossVal = parseFloat(customAuditGross);
                      if (!isNaN(grossVal)) {
                        triggerNexoraXAudit(`re-audit ${grossVal}`);
                      }
                    }}
                    className="px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-black font-mono font-black uppercase text-[9px] tracking-wider rounded-lg transition-colors cursor-pointer"
                  >
                    Recalculate
                  </button>
                </div>
              </div>

              {/* Receipt Style Mathematical Accounting Details */}
              <div className="p-4 bg-black/65 rounded-xl border border-neutral-900 space-y-3 font-mono text-[10px]">
                <div className="flex justify-between border-b border-white/5 pb-2 text-zinc-400">
                  <span>METRIC FIELD</span>
                  <span className="text-right">CALCULATED VALUE (GBP)</span>
                </div>
                
                <div className="flex justify-between items-center text-white">
                  <span>1. Gross Sourced Harvest:</span>
                  <span className="font-bold">£{auditBreakdown.gross.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>

                <div className="border-t border-neutral-900/65 my-1"></div>

                <div className="flex justify-between items-center text-amber-400">
                  <span>2. 1% 3% Community Allocation (Total):</span>
                  <span className="font-bold">-£{auditBreakdown.levy.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>

                {/* 3-way split logs matching instructions */}
                <div className="pl-4 space-y-1 text-[9px] text-zinc-500 border-l border-amber-500/30">
                  <div className="flex justify-between">
                    <span>• Charity 1 (Dunfermline) Split (33.33%):</span>
                    <span>£{auditBreakdown.charity1.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>• Charity 2 (Global) Split (33.33%):</span>
                    <span>£{auditBreakdown.charity2.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>• Charity 3 (UK) Split (33.33%):</span>
                    <span>£{auditBreakdown.charity3.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </div>

                <div className="border-t border-neutral-900/65 my-1"></div>

                <div className="flex justify-between items-center text-zinc-300">
                  <span>3. Taxable Principal Base:</span>
                  <span>£{auditBreakdown.taxBase.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>

                <div className="flex justify-between items-center text-rose-400">
                  <span>4. 20% Gavel Resource 10% Maintenance & Hosting Fee:</span>
                  <span className="font-bold">-£{auditBreakdown.liability20.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>

                <div className="border-t border-white/5 pt-2 flex justify-between items-center text-emerald-400 text-xs font-bold">
                  <span>5. Net finance Secure Payment Provider Settlement:</span>
                  <span className="text-sm font-black underline decoration-double">
                    £{auditBreakdown.netToSecurePayment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>

                <div className="pt-2 text-[8px] text-zinc-650 flex items-center justify-between border-t border-white/5 uppercase">
                  <span>NexoraX Bank: SecurePayment plc</span>
                  <span>Account: Secure Billing Provider // Sort: Verified Payment Configuration</span>
                </div>
              </div>
            </div>
          )}

          {/* INTENT-3: SYSTEM HEALTH AND DIAGNOSTICS */}
          {parsedIntent === 'status_checking' && (
            <div className="space-y-4">
              <span className="text-[10px] font-mono text-zinc-400 block uppercase">NexoraX Unified Node Health Monitor:</span>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: "SLA Latency Speed", value: "0.8s STANDBY", ok: true, detail: "enterprise-grade high-velocity frequency" },
                  { name: "Ingress Container Port", value: "PORT 3000 ONLINE", ok: true, detail: "Verified nginx reverse proxy routing" },
                  { name: "Secure Payment Provider Conduit", value: "Verified Payment Configuration / Secure Billing Provider", ok: true, detail: "Tax extracts & net settlements locked" },
                  { name: "Sponsorship Scheme", value: "Secure Credential Stored Server-Side Secure Payment Provider COMPLIANT", ok: true, detail: "Zero-cost NexoraX billing records protocol" },
                  { name: "Imperial Backup Sync", value: "CloudSync-02 SYNCED", ok: true, detail: "Real-time Google Drive NexoraX Resource Library sync" },
                  { name: "Telecom Freephone", value: "08000 622827 VERIFIED", ok: true, detail: "Active Planet Voice frequency link" }
                ].map((mn) => (
                  <div key={mn.name} className="p-3 bg-black/40 border border-neutral-900 rounded-xl space-y-1 hover:border-amber-500/20 transition-all duration-300">
                    <div className="flex items-center justify-between">
                      <span className="text-[9px] font-mono text-[#00c3ff] uppercase font-bold">{mn.name}</span>
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_#10b981]"></span>
                    </div>
                    <p className="text-white text-[11px] font-mono font-black uppercase tracking-wide mt-1">{mn.value}</p>
                    <p className="text-[8px] text-zinc-500 font-mono italic capitalize mt-0.5">{mn.detail}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>

      {/* ======================================================= */}
      {/* NEXORAX QUANTIZED VOCAL AUTOTUNE PANEL (NODE-09.2) */}
      {/* ======================================================= */}
      <div id="NexoraX-vocal-autotune-panel" className="mt-8 p-5 rounded-2xl bg-gradient-to-br from-[#020512] to-[#060a22]/90 border border-amber-500/20 shadow-[0_4px_30px_rgba(245,158,11,0.05)] relative z-20 space-y-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-white/5 pb-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500 animate-pulse" />
              <h4 className="text-xs font-mono font-black text-amber-400 uppercase tracking-widest">
                NexoraX Quantized Vocal Autotune System
              </h4>
            </div>
            <p className="text-[9px] font-mono text-zinc-500 uppercase">
              Web Audio Parallel Carrier Modulator & Speech Boundary Pitch-Quantization Matrix
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <span className={`text-[8px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full border transition-all ${
              isAutotuneEnabled 
                ? 'bg-amber-950/30 border-amber-500/40 text-amber-400 font-bold shadow-[0_0_8px_rgba(245,158,11,0.2)]' 
                : 'bg-zinc-900 border-neutral-800 text-zinc-650'
            }`}>
              {isAutotuneEnabled ? "● AUTOTUNE SYSTEM ACTIVE" : "○ BYPASS ACTIVE"}
            </span>

            {/* Premium Toggle Switch */}
            <button
              type="button"
              onClick={() => {
                const stepTarget = !isAutotuneEnabled;
                setIsAutotuneEnabled(stepTarget);
                setVoiceLog(`AUTOTUNE OVERRIDE: [${stepTarget ? 'ENABLED' : 'BYPASS-MUTED'}]`);
                if (!stepTarget) {
                  stopAutotuneSynth();
                }
              }}
              className={`w-11 h-6 rounded-full p-0.5 transition-colors duration-300 focus:outline-none cursor-pointer ${
                isAutotuneEnabled ? 'bg-amber-500' : 'bg-neutral-800'
              }`}
            >
              <div className={`bg-black w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ${
                isAutotuneEnabled ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </button>
          </div>
        </div>

        {/* Configurations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-stretch">
          
          {/* Left panel: Scale selectors & level */}
          <div className="md:col-span-5 space-y-3.5">
            <div className="space-y-1.5">
              <span className="text-[9px] font-mono text-[#00c3ff] font-bold uppercase tracking-wider block">
                1. Cybernetic Musical Scales:
              </span>
              <div className="grid grid-cols-2 gap-1.5">
                {[
                  { id: 'cyberpunk_minor', name: "Cyberpunk Minor" },
                  { id: 'NexoraX_major', name: "NexoraX Major" },
                  { id: 'pentatonic_classic', name: "Classic Pent" },
                  { id: 'matrix_space', name: "Matrix Space" }
                ].map((sc) => (
                  <button
                    key={sc.id}
                    type="button"
                    onClick={() => {
                      setAutotuneScale(sc.id as any);
                      setVoiceLog(`AUTOTUNE SCALE TUNED // TARGET: ${sc.name.toUpperCase()}`);
                    }}
                    className={`px-2.5 py-1.5 rounded-lg border text-[9px] font-mono text-left uppercase transition-all duration-250 cursor-pointer ${
                      autotuneScale === sc.id
                        ? 'bg-amber-500/10 border-amber-500/40 text-amber-400 font-bold'
                        : 'bg-black/40 border-neutral-900 text-zinc-500 hover:text-zinc-300 hover:border-zinc-800'
                    }`}
                  >
                    {autotuneScale === sc.id ? "• " : ""}{sc.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Carrier Synth Level Slider */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-[9px] font-mono">
                <span className="text-[#00c3ff] font-bold uppercase tracking-wider">
                  2. Synth Carrier Depth:
                </span>
                <span className="text-white font-black">{Math.round(autotuneVolume * 100)}% Amplitude</span>
              </div>
              <input
                type="range"
                min="0.0"
                max="0.6"
                step="0.05"
                value={autotuneVolume}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  setAutotuneVolume(val);
                }}
                disabled={!isAutotuneEnabled}
                className="w-full h-1.5 bg-neutral-950 rounded-lg appearance-none cursor-pointer accent-amber-500 disabled:opacity-40"
              />
            </div>
          </div>

          {/* Right panel: Live Scale Pitch Board & Wave preview */}
          <div className="md:col-span-7 flex flex-col justify-between bg-black/45 hover:bg-black/55 rounded-xl border border-neutral-900/40 p-4 transition-colors space-y-3.5">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[9px] font-mono text-[#00c3ff] font-bold uppercase tracking-wider block">
                  3. Quantized Scale Pitch-Board:
                </span>
                {isSpeaking && isAutotuneEnabled && (
                  <span className="text-[8px] font-mono text-amber-500 animate-pulse font-black uppercase">
                    ◄ Tracking word boundaries ...
                  </span>
                )}
              </div>
              
              {/* Note highlight board */}
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-1.5">
                {(SCALE_FREQUENCIES[autotuneScale] || SCALE_FREQUENCIES.cyberpunk_minor).map((note, index) => {
                  const isCurrent = currentQuantizedNote === note.name && isSpeaking && isAutotuneEnabled;
                  return (
                    <div
                      key={`${note.name}-${index}`}
                      className={`py-2 rounded-lg border text-center transition-all duration-150 flex flex-col items-center justify-center relative overflow-hidden ${
                        isCurrent
                          ? 'bg-amber-500 border-amber-400 text-black font-black scale-105 shadow-[0_0_12px_rgba(245,158,11,0.5)] z-10'
                          : 'bg-zinc-950 border-neutral-900 text-zinc-500'
                      }`}
                    >
                      {isCurrent && (
                        <div className="absolute inset-0 bg-white/20 animate-ping opacity-60 rounded-lg pointer-events-none" />
                      )}
                      <span className="text-xs font-mono font-bold tracking-tight">{note.name}</span>
                      <span className={`text-[8px] font-mono mt-0.5 leading-none ${isCurrent ? 'text-black' : 'text-zinc-650'}`}>
                        {note.hz.toFixed(1)}Hz
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Test autotuned vocal synthesize launchpad */}
            <div className="flex items-center justify-between border-t border-white/5 pt-3">
              <span className="text-[8px] font-mono text-zinc-600 block uppercase">
                Demo autotune playback directly to system speaker
              </span>
              <button
                type="button"
                onClick={() => {
                  const phrases = [
                    "NexoraX automated frequency calibrating complete. Synthesizer node initialized at zero error SLA.",
                    "Executing tiered extraction protocol. One percent global 3% Community Allocation distributed successfully. 10% Maintenance & Hosting Fee liability archived.",
                    "Vanguard neural matrix fully synchronized. Guardian zero two communications vault live.",
                    "NexoraX billing records mandate verification complete. Principal private secretary node active."
                  ];
                  const chosenPhrase = phrases[Math.floor(Math.random() * phrases.length)];
                  executeTTS(chosenPhrase, "AUTOTUNE_CALIBRATION_TEST");
                }}
                disabled={isSpeaking}
                className="px-3.5 py-1.5 bg-neutral-900 hover:bg-neutral-800 disabled:opacity-40 text-[#00c3ff] hover:text-white border border-neutral-800 hover:border-amber-500/30 font-mono font-bold uppercase text-[9px] tracking-wider rounded-lg transition-all flex items-center gap-1.5 cursor-pointer disabled:cursor-not-allowed"
              >
                <Radio className="w-3.5 h-3.5 text-amber-500 animate-pulse" />
                Test Voice Autotune
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* ======================================================= */}
      {/* EXECUTIVE COMMAND INPUT CONSOLE */}
      {/* Allows users to type / test intent keywords instantly */}
      {/* ======================================================= */}
      <div className="mt-8 p-4 rounded-2xl bg-neutral-950/60 border border-neutral-900 space-y-3 relative z-20">
        <label className="text-[9px] font-mono tracking-widest text-[#00c3ff] font-black uppercase flex items-center gap-1.5">
          <Terminal className="w-3.5 h-3.5 text-amber-500" />
          NexoraX Executive Command Input Console
        </label>
        
        <div className="flex gap-2.5">
          <input
            type="text"
            placeholder="Type 'deploy core matrix', 'run audit 2.8m', 'system status check'..."
            value={typedCommand}
            onChange={(e) => setTypedCommand(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && typedCommand.trim()) {
                parseTextForIntents(typedCommand);
                setSpeechText(`"${typedCommand}"`);
                setIsActive(true);
                setTypedCommand("");
              }
            }}
            className="flex-grow bg-black border border-neutral-800 focus:border-amber-500/70 focus:outline-none rounded-xl px-4 py-2 text-xs text-white font-mono placeholder:text-zinc-600"
          />
          <button
            type="button"
            onClick={() => {
              if (typedCommand.trim()) {
                parseTextForIntents(typedCommand);
                setSpeechText(`"${typedCommand}"`);
                setIsActive(true);
                setTypedCommand("");
              }
            }}
            className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-mono font-black uppercase text-[10px] tracking-wider rounded-xl transition-all shadow-[0_0_10px_rgba(245,158,11,0.15)] cursor-pointer hover:scale-[1.02]"
          >
            Dispatch
          </button>
        </div>

        {/* Instantly triggerable intent hotkeys */}
        <div className="flex flex-wrap gap-2 pt-1">
          <span className="text-[8px] font-mono text-zinc-650 uppercase self-center mr-1">Hotkeys:</span>
          {[
            { label: "🚀 Deploy Matrix", cmd: "deploy" },
            { label: "📊 Record Audit", cmd: "run financial audit on £2,820,000 gross" },
            { label: "🔍 Check Status", cmd: "query system status and health parameters" },
            { label: "✨ Concierge Node", cmd: "activate concierge node" },
            { label: "💼 Placement Subsidies", cmd: "placement candidate Client Resource Workflow Secure Invoice Register" }
          ].map((hk) => (
            <button
              key={hk.label}
              type="button"
              onClick={() => {
                parseTextForIntents(hk.cmd);
                setSpeechText(`"${hk.cmd}"`);
                setIsActive(true);
              }}
              className="px-2 py-1 text-[8px] font-mono uppercase bg-neutral-900 border border-neutral-800 text-zinc-400 hover:text-amber-400 hover:border-amber-500/30 rounded transition-colors cursor-pointer"
            >
              {hk.label}
            </button>
          ))}
        </div>
      </div>

      {/* ======================================================= */}
      {/* MODE BETA: REAL MICROPHONE VOICE RECORDER PANEL */}
      {/* ======================================================= */}
      <AnimatePresence mode="wait">
        {activeMode === 'mic_capture' && (
          <motion.div
            key="mic_capture"
            initial={{ height: 0, opacity: 0, marginTop: 0 }}
            animate={{ height: "auto", opacity: 1, marginTop: 32 }}
            exit={{ height: 0, opacity: 0, marginTop: 0 }}
            transition={{ type: "spring", stiffness: 350, damping: 32 }}
            className="space-y-6 pt-4 relative z-20 overflow-hidden"
          >
            <div className="flex items-center gap-2">
              <Mic className="w-5 h-5 text-amber-500 animate-pulse" />
              <span className="text-xs font-mono font-bold tracking-widest text-white uppercase bg-amber-950/20 border border-amber-500/25 px-2.5 py-1 rounded">
                High-Fidelity Audio Capture Bridge
              </span>
              <span className="text-[9px] font-mono text-zinc-500 ml-auto flex items-center gap-1.5 uppercase">
                <Activity className="w-3 h-3 text-emerald-500 animate-pulse" /> Latency: 0.8s
              </span>
            </div>

          <div className="p-5 rounded-2xl bg-black/40 border border-neutral-900 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            
            {/* Action buttons column */}
            <div className="md:col-span-5 flex flex-wrap gap-3">
              {voiceState === 'recording' ? (
                <button
                  type="button"
                  onClick={stopMicRecording}
                  className="px-5 py-3.5 rounded-xl bg-red-600 hover:bg-red-500 text-white font-mono font-black uppercase text-[10px] tracking-widest flex items-center gap-2 transition-all duration-200 animate-[pulse_1.5s_infinite] shadow-[0_0_20px_rgba(239,68,68,0.4)] border border-red-500 hover:scale-[1.02] cursor-pointer"
                >
                  <Square className="w-4 h-4 text-white" />
                  Stop &amp; Secure Capture
                </button>
              ) : voiceState === 'processing' ? (
                <button
                  type="button"
                  disabled
                  className="px-5 py-3.5 rounded-xl bg-indigo-955 text-indigo-300 font-mono font-black uppercase text-[10px] tracking-widest flex items-center gap-2 border border-indigo-500/30 opacity-75 cursor-not-allowed"
                >
                  <RefreshCw className="w-4 h-4 text-indigo-400 animate-spin" />
                  Processing Signal...
                </button>
              ) : (
                <button
                  type="button"
                  onClick={startMicRecording}
                  className="px-5 py-3.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-mono font-black uppercase text-[10px] tracking-widest flex items-center gap-2 transition-all duration-200 shadow-[0_0_20px_rgba(245,158,11,0.25)] border border-amber-400 hover:scale-[1.02] cursor-pointer"
                >
                  <Mic className="w-4 h-4 text-black" />
                  Start Live Recording
                </button>
              )}

              {recordedBlob && (
                <div className="flex gap-2 w-full mt-1.5">
                  <button
                    type="button"
                    onClick={playRecordedAudio}
                    className={`flex-1 px-4 py-2.5 rounded-lg border font-mono text-[10px] uppercase font-bold tracking-wider flex items-center justify-center gap-2 transition-all cursor-pointer ${
                      isPlaybackPlaying 
                        ? 'bg-rose-950/40 border-rose-500/30 text-rose-400' 
                        : 'bg-emerald-950/40 border-emerald-500/30 text-emerald-400 hover:bg-[#0c2e1f]'
                    }`}
                  >
                    <Play className="w-3.5 h-3.5" />
                    {isPlaybackPlaying ? "Pause Audio" : "Play Back Imprint"}
                  </button>

                  <button
                    type="button"
                    onClick={discardRecordedAudio}
                    className="p-2.5 rounded-lg border border-neutral-800 text-zinc-500 hover:text-rose-400 hover:border-rose-500/20 bg-neutral-950/40 transition-all cursor-pointer"
                    title="Discard audio blob"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Diagnostic Monitor Feed */}
            <div className="md:col-span-7 flex flex-col gap-1">
              <span className="text-[9px] font-mono tracking-widest uppercase text-zinc-500 font-bold">Diagnostics Channel:</span>
              <div className="p-3 bg-neutral-950/80 rounded-xl border border-neutral-900 font-mono text-[9px] text-zinc-400 flex items-start gap-2.5">
                {hasMicPermissionError ? (
                  <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0" />
                ) : isRecording ? (
                  <Activity className="w-4 h-4 text-emerald-500 animate-pulse shrink-0" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 text-amber-500 shrink-0" />
                )}
                <div className="flex-grow space-y-1">
                  <p className={hasMicPermissionError ? "text-rose-400 font-semibold" : "text-zinc-300 font-medium"}>
                    {micStateMsg}
                  </p>
                  <p className="text-[8px] text-zinc-650 text-neutral-500 uppercase">
                    Vetted through UK Gov sponsorship regulationsSC887095 protocols.
                  </p>
                </div>
              </div>
            </div>

          </div>

          {/* Cognitive Decryption / Automated Transcription Output */}
          {(isTranscribing || transcriptionResult) && (
            <div className="p-5 rounded-2xl bg-[#03081a]/50 border border-emerald-500/20 animate-fade-in space-y-3">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-emerald-400" />
                <span className="text-[10px] font-mono tracking-widest text-[#00ff88] uppercase font-bold">
                  Node-09.2 Quantum Voice Decrypted Transcription:
                </span>
                {isTranscribing && <RefreshCw className="w-3.5 h-3.5 text-emerald-500 animate-spin ml-auto" />}
              </div>

              {isTranscribing ? (
                <div className="flex items-center gap-3 py-2 font-mono text-[10px] text-zinc-500">
                  <div className="flex gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-bounce"></span>
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-bounce [animation-delay:0.2s]"></span>
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-bounce [animation-delay:0.4s]"></span>
                  </div>
                  <span>Frequencies analyzed. Synchronizing decrypted phonetics...</span>
                </div>
              ) : (
                <div className="p-4 bg-black/40 border border-neutral-900 rounded-xl">
                  <p className="text-white text-xs font-mono font-medium leading-relaxed italic">
                    {transcriptionResult}
                  </p>
                  <div className="mt-3 pt-2.5 border-t border-white/5 flex items-center justify-between text-[8px] font-mono text-zinc-500">
                    <span>REVENUE MATCH: ACCELERATED ONBOARDING SLOT ACTIVE</span>
                    <span className="text-emerald-400 font-black flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> SECURED COGNITIVE IMPRINT
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* NexoraX billing records & SecurePayment Close Integration block */}
          <AnimatePresence>
            {transcriptionResult && (
              <motion.div
                key="SecurePayment-checkout-bridge-active"
                initial={{ height: 0, opacity: 0, marginTop: 0 }}
                animate={{ height: "auto", opacity: 1, marginTop: 24 }}
                exit={{ height: 0, opacity: 0, marginTop: 0 }}
                transition={{ type: "spring", stiffness: 350, damping: 32 }}
                className="p-4 rounded-xl bg-emerald-950/20 border border-emerald-500/20 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 text-xs font-mono overflow-hidden"
              >
                <div className="space-y-1">
                  <p className="text-emerald-400 font-bold uppercase tracking-wider">SecurePayment NexoraX Gateway Integrated</p>
                  <p className="text-zinc-500 text-[10px] uppercase">
                    Replicating net settlements directly to account Sort Code: Verified Payment Configuration | Account: Secure Billing Provider
                  </p>
                </div>
                <a 
                  href="https://pay.SecurePayment.co/pay/9fd1aa7e-20b1-44b0-bf57-b75944dd5134" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="px-4 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-black font-sans font-black uppercase text-[9px] tracking-wider rounded-lg text-center transition-all duration-200 border border-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.25)] flex items-center justify-center gap-1.5"
                >
                  PROCEED TO SecurePayment COGNITIVE MATCH CHECKOUT
                  <ChevronRight className="w-3.5 h-3.5" />
                </a>
              </motion.div>
            )}
          </AnimatePresence>

        </motion.div>
      )}

      {/* ======================================================= */}
      {/* MODE ALPHA: COGNITIVE SOCIAL ENGAGEMENT SUITE (TTS) */}
      {/* ======================================================= */}
      {activeMode === 'synthesis' && (
        <motion.div
          key="synthesis"
          initial={{ height: 0, opacity: 0, marginTop: 0 }}
          animate={{ height: "auto", opacity: 1, marginTop: 32 }}
          exit={{ height: 0, opacity: 0, marginTop: 0 }}
          transition={{ type: "spring", stiffness: 350, damping: 32 }}
          className="pt-4 border-t border-amber-500/20 relative z-20 overflow-hidden"
        >
          <div className="flex items-center flex-wrap gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-amber-500 animate-pulse" />
            <h3 className="text-sm font-mono font-bold tracking-widest text-[#00c3ff] uppercase bg-cyan-950/20 border border-cyan-500/25 px-2.5 py-1 rounded">
              Node-09.3: NexoraX Engagement Social Engine
            </h3>
            
            {/* ACOUSTIC TTS SPEAKER MUTING */}
            <button
              type="button"
              onClick={() => {
                setIsTtsMuted(!isTtsMuted);
                if (isTtsMuted) {
                  if ('speechSynthesis' in window) {
                    window.speechSynthesis.cancel();
                    const utterance = new SpeechSynthesisUtterance("Vocal synthesis enabled.");
                    window.speechSynthesis.speak(utterance);
                  }
                } else {
                  if ('speechSynthesis' in window) {
                    window.speechSynthesis.cancel();
                  }
                }
              }}
              className={`text-[9px] font-mono ml-auto border px-2 py-[3px] rounded font-black uppercase cursor-pointer transition-all ${
                isTtsMuted 
                  ? 'bg-rose-950/30 border-rose-500/50 text-rose-450 hover:bg-rose-900/40' 
                  : 'bg-emerald-950/20 border-emerald-500/50 text-emerald-400 hover:bg-emerald-900/40'
              }`}
            >
              {isTtsMuted ? '🔇 TTS MUTED' : '🔊 TTS LIVE SPEAKER'}
            </button>

            <span className="text-[9px] font-mono text-cyan-400 border border-cyan-500/30 px-1.5 py-1 rounded font-black uppercase">
              Persona: Warm &amp; Persuasive
            </span>
          </div>

          <p className="text-xs text-slate-400 font-sans mb-6 leading-relaxed max-w-3xl">
            Designed specifically under direct authorization of the Director, Lead Director. This engine deploys targeted psychological loops to accelerate client conversions, blending authentic local geographic rapport with persuasive fiscal closeness.
          </p>

          {/* VOCAL PERSONA ENGINE MATRIX SELECTOR */}
          <div className="p-5 mb-6 rounded-2xl bg-gradient-to-br from-[#020410] to-[#04081c]/95 border border-amber-500/20 shadow-[0_4px_25px_rgba(245,158,11,0.03)]">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-amber-500 animate-pulse" />
              <span className="text-[10px] font-mono text-[#00c3ff] font-bold uppercase tracking-wider block">
                Vocal Persona Engine Matrix (Concierge-Class Integration)
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                type="button"
                onClick={() => {
                  setVocalPersona('NexoraX_director');
                  setVoiceLog("VOCAL PERSONA: DIRECTOR NEXORAX NODE [ACTIVE]");
                  setTimeout(() => {
                    executeTTS("This is the Office of the Director of NexoraX Digital. I am Director Lead Director. Our neural vanguard is fully operational.", "PERSONA_CHANGED");
                  }, 50);
                }}
                className={`flex items-start gap-3.5 p-4 rounded-xl border text-left cursor-pointer transition-all duration-300 ${
                  vocalPersona === 'NexoraX_director'
                    ? 'bg-amber-500/10 border-amber-500/50 text-amber-400 font-bold shadow-[0_0_15px_rgba(245,158,11,0.12)] border-amber-400/80 bg-neutral-900/50 shadow-amber-550/20'
                    : 'bg-black/40 border-neutral-900 text-zinc-500 hover:text-zinc-300'
                }`}
              >
                <div className="text-2xl mt-0.5">👑</div>
                <div>
                  <h4 className="text-xs uppercase font-mono tracking-wider font-extrabold text-amber-450">NexoraX Director</h4>
                  <p className="text-[10px] text-zinc-450 font-normal font-sans normal-case mt-1 leading-snug">
                    Director Lead Director. Deep baritone, militant, ultimate corporate gravity, uncompromising financial protocols.
                  </p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => {
                  setVocalPersona('concierge');
                  setVoiceLog("VOCAL PERSONA: CONCIERGE-CLASS [ACTIVE]");
                  // Play dynamic welcome immediately
                  setTimeout(() => {
                    executeTTS("Hello there! You’ve reached the NexoraX Sanctum. I’m your NexoraX Concierge. I make all the 'scary' technical stuff actually work for you!", "PERSONA_CHANGED");
                  }, 50);
                }}
                className={`flex items-start gap-3.5 p-4 rounded-xl border text-left cursor-pointer transition-all duration-300 ${
                  vocalPersona === 'concierge'
                    ? 'bg-amber-500/10 border-amber-500/50 text-amber-400 font-bold shadow-[0_0_15px_rgba(245,158,11,0.12)]'
                    : 'bg-black/40 border-neutral-900 text-zinc-500 hover:text-zinc-300'
                }`}
              >
                <div className="text-2xl mt-0.5">✨</div>
                <div>
                  <h4 className="text-xs uppercase font-mono tracking-wider font-extrabold">NexoraX Concierge</h4>
                  <p className="text-[10px] text-zinc-400 font-normal font-sans normal-case mt-1 leading-snug">
                    Concierge-Class Engagement Persona. Warm, sweet, simplifies complex deployments, eliminates friction with effortless charm.
                  </p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => {
                  setVocalPersona('cybernetic');
                  setVoiceLog("VOCAL PERSONA: CYBERNETIC DAEMON [ACTIVE]");
                  // Play cybernetic welcome immediately
                  setTimeout(() => {
                    executeTTS("Greetings from the imperial vanguard. System handshakes initialized. Standing by under zero point eight second SLA parameters.", "PERSONA_CHANGED");
                  }, 50);
                }}
                className={`flex items-start gap-3.5 p-4 rounded-xl border text-left cursor-pointer transition-all duration-300 ${
                  vocalPersona === 'cybernetic'
                    ? 'bg-amber-500/10 border-amber-500/50 text-amber-400 font-bold shadow-[0_0_15px_rgba(245,158,11,0.12)]'
                    : 'bg-black/40 border-neutral-900 text-zinc-500 hover:text-zinc-300'
                }`}
              >
                <div className="text-2xl mt-0.5">🤖</div>
                <div>
                  <h4 className="text-xs uppercase font-mono tracking-wider font-extrabold">Cybernetic Core</h4>
                  <p className="text-[10px] text-zinc-400 font-normal font-sans normal-case mt-1 leading-snug">
                    NexoraX System Matrix. High-precision system telemetry feedback, ledger audits, and zero-extraction proxy validation.
                  </p>
                </div>
              </button>
            </div>
          </div>

          {/* Input Matrix Setup */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            
            {/* Weather Selector */}
            <div className="p-4 rounded-xl bg-black/40 border border-neutral-900 flex flex-col gap-2">
              <label className="text-[10px] font-mono font-bold uppercase text-zinc-500 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-cyan-400" />
                1. Geographic Context Alignment
              </label>
              <div className="grid grid-cols-5 gap-1.5 mt-1">
                {weatherMatrix.map((opt, idx) => (
                  <button
                    key={opt.city}
                    type="button"
                    onClick={() => setSelectedCityIdx(idx)}
                    className={`py-1.5 rounded text-[10px] font-mono border transition-all cursor-pointer ${
                      selectedCityIdx === idx 
                        ? 'bg-cyan-950/60 border-cyan-400/80 text-cyan-400 font-black shadow-[0_0_12px_rgba(6,182,212,0.3)]' 
                        : 'bg-[#040913] border-neutral-900 text-slate-400 hover:border-neutral-700'
                    }`}
                  >
                    {opt.city}
                  </button>
                ))}
              </div>
              <div className="text-[10px] font-mono text-zinc-500 mt-1 italic flex items-center gap-1">
                <Sun className="w-3 h-3 text-amber-500" />
                <span>Target setting: {currentSelection.city} with {currentSelection.weather}</span>
              </div>
            </div>

            {/* Business Select / Input */}
            <div className="p-4 rounded-xl bg-black/40 border border-neutral-900 flex flex-col gap-2">
              <label className="text-[10px] font-mono font-bold uppercase text-zinc-500 flex items-center gap-1.5">
                <Building className="w-3.5 h-3.5 text-pink-400" />
                2. Target Corporate Profile
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Custom corporate target name..."
                  value={customBusiness}
                  onChange={(e) => setCustomBusiness(e.target.value)}
                  className="bg-[#030712] border border-neutral-800 rounded-lg px-3 py-1.5 text-xs text-white max-w-full focus:outline-none focus:border-cyan-500/70 font-sans flex-grow"
                />
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {businessTemplates.map((biz) => (
                  <button
                    key={biz}
                    type="button"
                    onClick={() => setCustomBusiness(biz)}
                    className="px-2 py-0.5 rounded text-[9px] font-mono border border-neutral-900 hover:border-zinc-700 bg-neutral-950 text-zinc-400 hover:text-white cursor-pointer"
                  >
                    {biz}
                  </button>
                ))}
              </div>
            </div>

          </div>

          {/* Command Striker Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            
            {/* Persona Handshake Welcome */}
            <button
              type="button"
              onClick={() => {
                const text = EngagementEngine.welcome();
                executeTTS(text, "SOCIAL_ENGAGEMENT_WELCOME");
              }}
              disabled={isSpeaking}
              className="flex flex-col items-center justify-center p-4 rounded-2xl bg-[#030113] border border-amber-500/30 hover:border-amber-400 text-center transition-all duration-300 relative group cursor-pointer hover:shadow-[0_0_20px_rgba(245,158,11,0.08)] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-amber-950/40 border border-amber-500/40 group-hover:scale-105 transition-transform duration-300 mb-2">
                <Sparkles className="w-4 h-4 text-amber-400 group-hover:animate-bounce" />
              </div>
              <span className="text-[11px] font-mono font-bold uppercase text-white tracking-wider">
                [0] Welcome Handshake
              </span>
              <span className="text-[9px] font-mono text-zinc-500 mt-1 uppercase">
                Vocal Intimacy Setup
              </span>
            </button>

            {/* Detect Context Button */}
            <button
              type="button"
              onClick={handleDetectContext}
              disabled={isSpeaking}
              className="flex flex-col items-center justify-center p-4 rounded-2xl bg-[#040815] border border-cyan-500/20 hover:border-cyan-400/60 text-center transition-all duration-300 relative group cursor-pointer hover:shadow-[0_0_20px_rgba(0,195,255,0.08)] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-cyan-950/50 border border-cyan-400/30 group-hover:scale-105 transition-transform duration-300 mb-2">
                <Sun className="w-5 h-5 text-cyan-400 group-hover:rotate-12 transition-transform animate-[spin_5s_linear_infinite]" />
              </div>
              <span className="text-[11px] font-mono font-bold uppercase text-white tracking-wider">
                [1] Detect Context
              </span>
              <span className="text-[9px] font-mono text-zinc-500 mt-1 uppercase">
                Rapport Loop
              </span>
            </button>

            {/* Flirtatious Engagement Button */}
            <button
              type="button"
              onClick={handleFlirtatiousEngagement}
              disabled={isSpeaking}
              className="flex flex-col items-center justify-center p-4 rounded-2xl bg-[#050410] border border-pink-500/20 hover:border-pink-400/60 text-center transition-all duration-300 relative group cursor-pointer hover:shadow-[0_0_20px_rgba(236,72,153,0.08)] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-pink-950/50 border border-pink-500/30 group-hover:scale-105 transition-transform duration-300 mb-2">
                <Heart className="w-4 h-4 text-pink-400 group-hover:animate-pulse" />
              </div>
              <span className="text-[11px] font-mono font-bold uppercase text-white tracking-wider">
                [2] Flirtatious FaaS
              </span>
              <span className="text-[9px] font-mono text-zinc-500 mt-1 uppercase">
                Deal Acceleration
              </span>
            </button>

            {/* Unrefusable SecurePayment Close */}
            <button
              type="button"
              onClick={handleTheClose}
              disabled={isSpeaking}
              className="flex flex-col items-center justify-center p-4 rounded-2xl bg-[#080603] border border-amber-500/20 hover:border-amber-400/60 text-center transition-all duration-300 relative group cursor-pointer hover:shadow-[0_0_20px_rgba(245,158,11,0.08)] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-amber-950/50 border border-amber-500/30 group-hover:scale-105 transition-transform duration-300 mb-2">
                <CreditCard className="w-4 h-4 text-amber-400" />
              </div>
              <span className="text-[11px] font-mono font-bold uppercase text-white tracking-wider">
                [3] The Close
              </span>
              <span className="text-[9px] font-mono text-zinc-500 mt-1 uppercase">
                NexoraX SecurePayment Bridge
              </span>
            </button>

          </div>

          {/* SecurePayment Close Callout Anchor */}
          <AnimatePresence>
            {speechText.includes("SecurePayment Checkout") && (
              <motion.div
                key="alpha-SecurePayment-checkout-overlay"
                initial={{ height: 0, opacity: 0, marginTop: 0 }}
                animate={{ height: "auto", opacity: 1, marginTop: 20 }}
                exit={{ height: 0, opacity: 0, marginTop: 0 }}
                transition={{ type: "spring", stiffness: 350, damping: 32 }}
                className="p-4 rounded-xl bg-amber-950/20 border border-amber-500/30 animate-pulse flex items-center justify-between text-xs font-mono overflow-hidden"
              >
                <div className="flex items-center gap-3">
                  <span className="p-2 bg-amber-500/10 rounded-lg text-amber-400">🤝</span>
                  <div>
                    <p className="text-white font-bold uppercase tracking-wider">Direct SecurePayment Open API Bridge Ready</p>
                    <p className="text-zinc-400 text-[10px] mt-0.5">Sort Code: Verified Payment Configuration | Account: Secure Billing Provider</p>
                  </div>
                </div>
                <a 
                  href="https://pay.SecurePayment.co/pay/9fd1aa7e-20b1-44b0-bf57-b75944dd5134" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-sans font-black uppercase text-[10px] tracking-wider rounded-lg border border-amber-400 transition-all duration-200 flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-amber-500 shadow-[0_0_15px_rgba(245,158,11,0.3)] text-center justify-center"
                >
                  🚀 LAUNCH SecurePayment SECURE CHECKOUT
                  <ChevronRight className="w-3.5 h-3.5" />
                </a>
              </motion.div>
            )}
          </AnimatePresence>

        </motion.div>
      )}
    </AnimatePresence>
      {/* ======================================================= */}

      {/* Dynamic Status / Verification Footer Links */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-white/5 pt-6">
        {[
          { 
            title: "Voice Traffic Flow", 
            value: isActive ? (isRecording ? "CAPTURE STREAM ACTIVE" : "4.8kb/s AES-256") : "OFFLINE SECURED", 
            color: isRecording ? "text-emerald-400" : (isActive ? "text-amber-400" : "text-neutral-500")
          },
          { 
            title: "Planet Voice Frequency", 
            value: "08000 622827 VERIFIED", 
            color: "text-zinc-400" 
          },
          { 
            title: "Sponsorship & Access", 
            value: "Secure Credential Stored Server-Side / Secure Payment Provider DIRECT", 
            color: "text-amber-500 font-bold" 
          }
        ].map(node => (
          <div 
            key={node.title} 
            className="bg-neutral-950/70 p-4 border border-neutral-900 rounded-xl flex flex-col justify-between hover:border-amber-500/20 transition-all duration-300"
          >
            <span className="text-[9px] font-mono font-bold uppercase tracking-widest text-[#00c3ff]">{node.title}</span>
            <span className={`text-[10px] font-mono font-bold uppercase tracking-wide mt-2 ${node.color}`}>{node.value}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
};
